import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/utils/cn";
import { Icon } from "@/components/Icon/Icon";
import { logEvent } from "@/lib/eventLogger";
import {
  COMPOUND_LIB,
  FOUNDATION_COMPOUNDS,
} from "@/services/mock";
import { AIBanner } from "./AutoFormulate/AIBanner";
import { HeatmapRow } from "./AutoFormulate/HeatmapRow";
import { ScanBeam } from "./AutoFormulate/ScanBeam";
import { StatusFeed } from "./AutoFormulate/StatusFeed";
import { ChangeSymbol } from "./AutoFormulate/ChangeSymbol";
import { UndoButton } from "./AutoFormulate/UndoButton";
import type { AutoFormulateState } from "@/hooks/useAutoFormulate";
import type { AutoFormulateRunOpts } from "./AutoFormulate/AutoFormulateButton";
import type { CompositionRow } from "@/services/composition_api";
import type { FormulationEntry, CompoundLibEntry } from "@/utils/types/compound";
import type { Target } from "@/utils/types/flavor";
import type { ReplacementPreview } from "@/utils/formulation/computeCurrent";
import type { CostBreakdown } from "@/utils/formulation/computeCost";
import { FlaskBar } from "./FlaskBar";
import { FlaskRow, type ConcUnit, type FlaskRowProps } from "./FlaskRow";
import {
  useReplacementBadge,
  evictReplacementBadge,
} from "@/hooks/useReplacementBadge";
import { ReplacementPreviewBanner } from "./ReplacementPreviewBanner";
import { ReplacementSubRows } from "./ReplacementSubRows";
import {
  SupplyModalHost,
  type SupplyModalState,
} from "@/components/Modals/Supply/SupplyModalHost";
import type { SupplyModalKind } from "@/components/Modals/Supply/RowSupplyIcons";
import { CompositionAside } from "./CompositionAside";
import { FlaskToolbar } from "./FlaskToolbar";
import type {
  CompareCatalog,
  CompareFormula,
  ComputeCompareStats,
} from "@/utils/types/compare";
import type { VersionDetail } from "@/utils/types/flavorVersion";
import type { AmountUnit } from "@/services/formula_api";
import { amountSortKey } from "@/utils/formulation/normalizeAmountUnit";
import {
  lookupServerCompound,
  lookupServerLens,
  useRegistryVersion,
} from "@/utils/serverCompounds";
import { useSubstanceSupplyFlags } from "@/hooks/useSubstanceSupplyFlags";
import { bucketForLens } from "@/utils/lens";
import type { SubstanceLensInfo } from "@/utils/types/substance";
import type { PerItemReplacements } from "@/services/workbench_api";

type Lens = "role" | "solvent" | "chem";

const ALL_COMPOUNDS = [...COMPOUND_LIB, ...FOUNDATION_COMPOUNDS];
const compoundById = (id: string): CompoundLibEntry | undefined =>
  ALL_COMPOUNDS.find((c) => c.id === id) ?? lookupServerCompound(id);

const EMPTY_LENS: SubstanceLensInfo = {
  primaryRole: null,
  technicalRoles: [],
  solubility: null,
  chemClass: null,
  family: null,
  flavorLabels: [],
};

const LENS_TABS: { id: Lens; label: string }[] = [
  { id: "role", label: "Technical Role" },
  { id: "solvent", label: "Solvent Compatibility" },
  { id: "chem", label: "Chemical Class" },
];

// Rules of Hooks forbids calling hooks inside a .map() callback. This thin
// wrapper lets each formulation row call useReplacementBadge at the top of
// its own component tree, which is legal.
type FlaskRowWithBadgeProps = Omit<FlaskRowProps, "badge" | "badgeDifficultyText"> & {
  perItem: PerItemReplacements | undefined;
  formulaId: number | null;
};

function FlaskRowWithBadge(props: FlaskRowWithBadgeProps) {
  const { perItem, formulaId, ...flaskRowProps } = props;
  const badge = useReplacementBadge({
    compoundId: flaskRowProps.entry.compoundId,
    target: flaskRowProps.target ?? null,
    perItem,
    flags: flaskRowProps.flags ?? null,
    formulaId,
  });
  const difficultyText = useMemo(() => {
    const f = flaskRowProps.flags;
    if (!f) return undefined;
    const noSupplier = f.marketCheapest == null;
    const danger = f.safetySummary?.signal === "Danger";
    if (noSupplier && danger) return "No supplier registered · Danger hazard";
    if (noSupplier) return "No supplier registered";
    if (danger) return "Danger hazard";
    return undefined;
  }, [flaskRowProps.flags]);
  return (
    <FlaskRow
      {...flaskRowProps}
      badge={badge}
      badgeDifficultyText={difficultyText}
    />
  );
}

type FlaskPanelProps = {
  flavorId: string | null;
  formulaId?: number | null;
  formulation: FormulationEntry[];
  foundation?: FormulationEntry[];
  target: Target | null | undefined;
  loss: number | null;
  cost: CostBreakdown | null;
  useMedianFallback: boolean;
  setUseMedianFallback: (next: boolean) => void;
  medianPricePerGramUsd: number | null;
  preview: ReplacementPreview | null;
  locked?: boolean;
  onChangeConc: (id: string, value: number, unit?: AmountUnit) => void;
  onRemove: (id: string) => void;
  onPreview: (preview: ReplacementPreview | null) => void;
  onApplyPreview: () => void;
  onCancelPreview: () => void;
  onAdd?: (id: string) => void;
  onSeeBatchRecord?: () => void;
  // Composition aside — when true, the FlaskPanel body splits and the food
  // composition reference docks on the LEFT. The Composition button in the
  // header reflects this state. Both are wired by the parent so the toggle
  // can survive remounts and be addressed from anywhere in the workbench.
  showComposition?: boolean;
  onToggleComposition?: () => void;
  // Composition data — fed from useFlavorComposition. When hasComposition
  // is false the FlaskToolbar renders the button disabled with a tooltip;
  // when true, the aside renders compositionRows beside the flask rows.
  hasComposition?: boolean;
  compositionRows?: CompositionRow[];
  compositionFoodName?: string | null;
  compositionNfoodVersionId?: number | null;
  // Compare wiring — passed through to <FlaskToolbar>'s <CompareControl>.
  // Selection state lives one level up so the FlaskPanel ↔ ComparePanel
  // swap can be driven by the same source of truth.
  compareCatalog?: CompareCatalog;
  compareSelected?: CompareFormula[];
  onCompareToggle?: (f: CompareFormula) => void;
  onCompareClear?: () => void;
  computeCompareStats?: ComputeCompareStats;
  /** Forwarded to the popover so the similar/AI sections can show
   *  "Loading…" copy while server suggestions are still in-flight. */
  suggestionsLoading?: boolean;
  /** Fired after a successful version restore with the new VersionDetail.
   *  Allows WorkbenchPage to patch the active variant's in-memory formulation. */
  onRestoreSuccess?: (detail: VersionDetail) => void;
  /** Fired after a successful import commit with the new VersionDetail.
   *  Allows WorkbenchPage to patch the active variant's in-memory formulation. */
  onImportCommitSuccess?: (detail: VersionDetail) => void;
  // Export — forwarded to FlaskToolbar for xlsx filename.
  flavorName?: string;
  formulaName?: string;
  /** Auto-Formulate state machine — driven by useAutoFormulate at the
   *  workbench level. When `phase === "formulating"` the flask body
   *  swaps to a heatmap stack; when `phase === "review"` rows show
   *  per-row diff symbols and Undo buttons; the banner renders in both. */
  aiState?: AutoFormulateState;
  /** Fired by the Auto-Formulate button. */
  onAutoFormulate: (opts: AutoFormulateRunOpts) => void;
  /** Disabled signal for the Auto-Formulate button (e.g. locked variant). */
  autoFormulateDisabled?: boolean;
  /** Hover tooltip surfaced when `autoFormulateDisabled` is true. */
  autoFormulateDisabledReason?: string;
  /**
   * Per-compound replacement data from the replacements payload.
   * Keyed by server compoundId ("compound:<rowid>").
   * Threaded into ReplacementSubRows for drop/single/multi sections.
   */
  perItemByCompoundId?: Record<string, PerItemReplacements>;
  /**
   * Optional target-profile vector sourced from useWorkbenchViz. When present
   * (target food cached for the active pin), the AIBanner renders the real
   * target heatmap row during AF Running.
   */
  targetProfile?: number[] | null;
  /**
   * Optional current m(S) profile vector sourced from useWorkbenchViz. When
   * present, the AIBanner renders the live Current row during AF Running.
   */
  currentProfileVec?: number[] | null;
};

type CostUnitId = "10g" | "100g" | "1kg" | "batch";

export function FlaskPanel({
  flavorId,
  formulaId = null,
  formulation,
  foundation = [],
  target,
  loss,
  cost,
  useMedianFallback,
  setUseMedianFallback,
  medianPricePerGramUsd,
  preview,
  locked = false,
  onChangeConc,
  onRemove,
  onPreview,
  onApplyPreview,
  onCancelPreview,
  onAdd,
  onSeeBatchRecord,
  showComposition = false,
  onToggleComposition,
  hasComposition = false,
  compositionRows = [],
  compositionFoodName,
  compositionNfoodVersionId = null,
  compareCatalog,
  compareSelected = [],
  onCompareToggle,
  onCompareClear,
  computeCompareStats,
  suggestionsLoading,
  onRestoreSuccess,
  onImportCommitSuccess,
  flavorName,
  formulaName,
  aiState,
  onAutoFormulate,
  autoFormulateDisabled = false,
  autoFormulateDisabledReason,
  perItemByCompoundId = {},
  targetProfile = null,
  currentProfileVec = null,
}: FlaskPanelProps) {
  const [lens, setLens] = useState<Lens>("role");
  const [costUnit, setCostUnit] = useState<CostUnitId>("100g");
  const [units, setUnits] = useState<Record<string, ConcUnit>>({});
  const [expandedFor, setExpandedFor] = useState<string | null>(null);
  const [supplyModal, setSupplyModal] = useState<SupplyModalState>(null);
  const openSupply = (kind: SupplyModalKind, id: string) => {
    logEvent("flavor_workbench_info_modal_opened", {
      kind,
      flavor_id: flavorId,
      formula_id: formulaId,
      compound_id: id,
    });
    setSupplyModal({ kind, id });
  };
  const closeSupply = () => setSupplyModal(null);

  const toggleExpand = (id: string) => {
    setExpandedFor((prev) => {
      if (prev === id) {
        onCancelPreview();
        return null;
      }
      // Fire on the open-transition only — close-by-toggle and switches are
      // not "user opened a new replacement list" intent.
      logEvent("flavor_workbench_replacements_opened", {
        flavor_id: flavorId,
        formula_id: formulaId,
        compound_id: id,
      });
      // Switching to a different row — also clear its preview
      if (preview && preview.fromId !== id) onCancelPreview();
      return id;
    });
  };

  const closeExpand = () => {
    onCancelPreview();
    setExpandedFor(null);
  };

  // Track previous loss for the delta chip on the loss tile.
  const lossRef = useRef<number | null>(null);
  const [lossPrev, setLossPrev] = useState<number | null>(null);
  useEffect(() => {
    if (preview) return;
    if (
      lossRef.current !== null &&
      loss !== null &&
      lossRef.current !== loss
    ) {
      setLossPrev(lossRef.current);
    }
    lossRef.current = loss;
  }, [loss, preview]);

  // FlaskRow's on-blur normalization calls this with the post-normalized
  // unit, which means the saved amountUnit on the entry will change. Any
  // stale cycle-override in `units` for that row no longer makes sense —
  // drop it so the next render reads the new saved unit directly.
  const onChangeConcAndClearOverride = (
    id: string,
    value: number,
    unit?: AmountUnit,
  ) => {
    logEvent("flavor_workbench_formulation_altered", {
      action: "concentration_changed",
      flavor_id: flavorId,
      formula_id: formulaId,
      compound_id: id,
      amount_value: String(value),
      amount_unit: unit ?? null,
    });
    onChangeConc(id, value, unit);
    if (unit !== undefined) {
      setUnits((u) => {
        if (!(id in u)) return u;
        const next = { ...u };
        delete next[id];
        return next;
      });
    }
  };

  // Wrap onRemove so the "removed" analytics event fires with full context
  // before delegating to the parent mutator.
  const onRemoveWithAnalytics = (id: string) => {
    logEvent("flavor_workbench_formulation_altered", {
      action: "removed",
      flavor_id: flavorId,
      formula_id: formulaId,
      compound_id: id,
      amount_value: null,
      amount_unit: null,
    });
    onRemove(id);
  };

  const cycleUnit = (compoundId: string, role: string | undefined) => {
    const cycle = role === "flavoring" ? (["ppm", "ppb", "ppt"] as const) : (["mg", "g"] as const);
    const cur =
      units[compoundId] || (role === "flavoring" ? "ppm" : "mg");
    const idx = (cycle as readonly string[]).indexOf(cur);
    const next = cycle[(idx + 1) % cycle.length] as ConcUnit;
    setUnits((u) => ({ ...u, [compoundId]: next }));
  };

  const flavorTotal = formulation.reduce((s, f) => s + f.ppm, 0);
  const foundTotal = foundation.reduce((s, f) => s + f.ppm, 0);
  const grandTotal = flavorTotal + foundTotal;

  // Same family-aware decreasing-concentration ordering as the lens-bucketed
  // flavor groups (see `groups` memo below). The Fixed Ingredients section
  // isn't grouped by lens, so it's a single sorted list.
  const sortedFoundation = useMemo(
    () =>
      [...foundation].sort((x, y) => {
        const kx = amountSortKey(x.ppm, x.amountUnit);
        const ky = amountSortKey(y.ppm, y.amountUnit);
        return kx[0] - ky[0] || kx[1] - ky[1];
      }),
    [foundation],
  );

  // Group flavor rows by lens.
  type Row = { f: FormulationEntry; c: CompoundLibEntry };
  type Group = { key: string; label: string; sortIdx: number; rows: Row[] };

  const registryVersion = useRegistryVersion();
  const groups = useMemo<Group[]>(() => {
    void registryVersion;
    const buckets = new Map<string, Group>();
    const push = (key: string, label: string, sortIdx: number, row: Row) => {
      if (!buckets.has(key)) buckets.set(key, { key, label, sortIdx, rows: [] });
      buckets.get(key)!.rows.push(row);
    };
    for (const f of formulation) {
      const c = compoundById(f.compoundId);
      if (!c) continue;
      const lensInfo = lookupServerLens(f.compoundId) ?? EMPTY_LENS;
      const { key, label, sortIdx } = bucketForLens(lensInfo, lens);
      push(key, label, sortIdx, { f, c });
    }
    const arr = [...buckets.values()].sort(
      (a, b) => a.sortIdx - b.sortIdx || a.label.localeCompare(b.label)
    );
    // Within each group: volume → mass → parts-per (family priority), then
    // by descending magnitude in the chain's base unit, so "1 ppm" sorts
    // above "500 ppb" and a "mL" row sorts above any ppm row regardless
    // of magnitude.
    arr.forEach((g) =>
      g.rows.sort((x, y) => {
        const kx = amountSortKey(x.f.ppm, x.f.amountUnit);
        const ky = amountSortKey(y.f.ppm, y.f.amountUnit);
        return kx[0] - ky[0] || kx[1] - ky[1];
      }),
    );
    return arr;
  }, [formulation, lens, registryVersion]);

  const aiPhase = aiState?.phase ?? "idle";
  // Active = the worker is computing OR we're staging proposed changes
  // one by one. `starting` covers the brief pre-SSE window so the
  // heatmap reveal is in place before the `initial` event lands. Note
  // that "formulating" used to live in this set historically; that
  // legacy name was renamed to `starting`/`running` and never updated
  // here, leaving the heatmap reveal effectively dead. Re-anchored now.
  const aiActive =
    aiPhase === "starting" ||
    aiPhase === "running" ||
    aiPhase === "applying";
  const aiReviewing = aiPhase === "review";

  // While the AI is running the composition aside is force-opened by the
  // workbench-level state machine (so the user sees the heatmap reveal in
  // its proper context). The aside-visibility predicate must therefore mirror
  // both the toggle and the forced-open path.
  const showCompositionEffective = showComposition || aiActive;

  // Index AI changes by compoundId for cheap per-row lookups.
  const aiChangesByCompoundId = useMemo(() => {
    const map = new Map<string, NonNullable<typeof aiState>["changes"][number]>();
    if (!aiState) return map;
    for (const ch of aiState.changes) {
      map.set(ch.compoundId, ch);
    }
    return map;
  }, [aiState]);

  // AF profile lookup — keyed by formulation compoundId ("compound:<id>" or
  // "food:<id>") matching the format used in the SSE initial event. Returns
  // null for items not yet observed by the AF flow or for skipped compounds.
  const lookupAfProfile = (id: string): number[] | null =>
    aiState?.ingredientProfiles.get(id) ?? null;

  // hasComposition is now a server-driven flag (true when the flavor has a
  // TARGET public.food AND food_compound rows for the configured nfood
  // version). Mock target.composition is no longer consulted.
  const compositionVisible = showCompositionEffective && hasComposition;
  // When the Composition aside is open, the row layout drops the Supply/
  // Safety (Info) column so the remaining columns don't compress against the
  // 280px aside. The column header makes the same cut.
  const compact = compositionVisible;
  const inFlaskIds = useMemo(
    () => new Set(formulation.map((f) => f.compoundId)),
    [formulation]
  );

  // Supply flags batch — one call per (flavorId, distinct substance set).
  // The hook is enabled only when flavorId !== null AND the key list is
  // non-empty, so adding the first row triggers the fetch.
  const supplyKeys = useMemo(
    () => Array.from(new Set([
      ...formulation.map((f) => f.compoundId),
      ...foundation.map((f) => f.compoundId),
    ])),
    [formulation, foundation],
  );

  const candidateKeys = useMemo(() => {
    const out = new Set<string>();
    for (const perItem of Object.values(perItemByCompoundId)) {
      for (const set of [...perItem.singles, ...perItem.multis]) {
        for (const it of set.items) {
          // Server emits schema-qualified table names; client substance keys
          // use bare "compound"/"food".
          const kind =
            it.itemNdbTable === "public.compound"
              ? "compound"
              : it.itemNdbTable === "public.food"
                ? "food"
                : null;
          if (kind) out.add(`${kind}:${it.itemRowid}`);
        }
      }
    }
    return out;
  }, [perItemByCompoundId]);

  const supplyKeysWithCandidates = useMemo(
    () => Array.from(new Set([...supplyKeys, ...candidateKeys])),
    [supplyKeys, candidateKeys],
  );

  const flagsQ = useSubstanceSupplyFlags(flavorId, supplyKeysWithCandidates);
  const flagsBySubstance = flagsQ.data ?? {};
  // Per-row loading state — a row is "loading" if its own entry isn't in
  // the per-substance cache yet. With per-substance React Query caching,
  // adding a new compound only marks that one row as loading; the rest
  // stay rendered from cache.
  const isFlagLoading = (id: string): boolean =>
    flavorId !== null && flagsBySubstance[id] === undefined;

  const lastRowIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const currentIds = new Set([
      ...formulation.map((f) => f.compoundId),
      ...foundation.map((f) => f.compoundId),
    ]);
    for (const prev of lastRowIdsRef.current) {
      if (!currentIds.has(prev)) {
        evictReplacementBadge(formulaId ?? null, prev);
      }
    }
    lastRowIdsRef.current = currentIds;
  }, [formulation, foundation, formulaId]);

  return (
    <Fragment>
      <div className="panel flask-panel">
        {/* Header — just the panel label + toolbar (or batch-record CTA when locked).
            Both sides are flex-shrink: 0 so a narrow panel never compresses the
            title or toolbar buttons; the workbench page width clamp ensures
            there's room for both.
            NOTE: whiteSpace: nowrap is scoped to the specific elements that
            need it (the "Current flask" label, button labels) — NOT applied
            on the wrapper divs. CSS white-space inherits, so cascading nowrap
            from a parent breaks the popover descendants (Compare/History/
            Import/Export popups) by preventing their description text from
            wrapping inside the popover width. */}
        <div className="flask-header">
          <div className="hstack gap-2" style={{ flexShrink: 0 }}>
            <Icon name="flask" size={14} />
            <span className="label" style={{ whiteSpace: "nowrap" }}>
              Current flask
            </span>
          </div>
          <div className="hstack gap-2" style={{ flexShrink: 0 }}>
            {locked ? (
              <button
                type="button"
                className="btn xs"
                onClick={onSeeBatchRecord}
                title="Open the pilot-batch record for this version"
                style={{ fontWeight: 500 }}
              >
                <Icon name="beaker" size={11} />
                See batch record
              </button>
            ) : (
              <FlaskToolbar
                formulation={formulation}
                foundation={foundation}
                showComposition={showCompositionEffective}
                onToggleComposition={onToggleComposition}
                hasComposition={hasComposition}
                flavorId={flavorId}
                formulaId={formulaId}
                flavorName={flavorName}
                formulaName={formulaName}
                compareCatalog={compareCatalog}
                compareSelected={compareSelected}
                onCompareToggle={onCompareToggle}
                onCompareClear={onCompareClear}
                computeCompareStats={computeCompareStats}
                suggestionsLoading={suggestionsLoading}
                onRestoreSuccess={onRestoreSuccess}
                onImportCommitSuccess={onImportCommitSuccess}
                onAutoFormulate={onAutoFormulate}
                autoFormulateDisabled={
                  autoFormulateDisabled || aiActive || aiReviewing
                }
                autoFormulateDisabledReason={autoFormulateDisabledReason}
              />
            )}
          </div>
        </div>

        {/* Body — composition aside on the LEFT (when toggled), then the
            main column with KPI/bar strip + preview banner + rows. */}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "row",
            overflow: "hidden",
          }}
        >
          {compositionVisible && (
            <CompositionAside
              rows={compositionRows}
              foodName={compositionFoodName ?? "Reference food"}
              sourceLabel={
                compositionNfoodVersionId !== null && compositionNfoodVersionId !== undefined
                  ? `nfood v${compositionNfoodVersionId}`
                  : null
              }
              inFlaskIds={inFlaskIds}
              onClose={onToggleComposition!}
              onAdd={onAdd}
              aiActive={aiActive}
            />
          )}

          <div
            style={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {/* KPI tiles + segmented bar strip — hidden while AI is running
                so the AIBanner can take over the same vertical real estate
                with the live profile diff. */}
            {!aiActive && (
              <FlaskBar
                formulation={formulation}
                foundation={foundation}
                loss={loss}
                lossPrev={lossPrev}
                hasTarget={!!target}
                cost={cost}
                costUnit={costUnit}
                setCostUnit={setCostUnit}
                useMedianFallback={useMedianFallback}
                setUseMedianFallback={setUseMedianFallback}
                medianPricePerGramUsd={medianPricePerGramUsd}
                resolveName={(id) => compoundById(id)?.name ?? id}
              />
            )}

            {preview && !aiActive && !aiReviewing && (
              <ReplacementPreviewBanner
                preview={preview}
                onApply={() => {
                  logEvent("flavor_workbench_replacement_resolved", {
                    outcome: "accepted",
                    flavor_id: flavorId,
                    formula_id: formulaId,
                    kind: preview.isDrop ? "drop" : "swap",
                    from_compound_id: preview.fromId,
                    to_compound_ids: preview.toIds.join(",") || null,
                  });
                  onApplyPreview();
                }}
                onCancel={() => {
                  logEvent("flavor_workbench_replacement_resolved", {
                    outcome: "cancelled",
                    flavor_id: flavorId,
                    formula_id: formulaId,
                    kind: preview.isDrop ? "drop" : "swap",
                    from_compound_id: preview.fromId,
                    to_compound_ids: preview.toIds.join(",") || null,
                  });
                  onCancelPreview();
                }}
              />
            )}

            {aiState && aiPhase !== "idle" && (
              <AIBanner
                phase={
                  aiPhase === "running" ||
                  aiPhase === "starting" ||
                  aiPhase === "applying"
                    ? "formulating"
                    : aiPhase === "failed"
                    ? "failed"
                    : "review"
                }
                statusLog={aiState.statusLog}
                changes={aiState.changes}
                onAccept={() => {
                  logEvent("flavor_workbench_auto_formulate_resolved", {
                    outcome: "accepted",
                    flavor_id: flavorId,
                    formula_id: formulaId,
                    change_count: aiState.changes.length,
                  });
                  aiState.accept();
                }}
                onReject={() => {
                  logEvent("flavor_workbench_auto_formulate_resolved", {
                    outcome: "rejected",
                    flavor_id: flavorId,
                    formula_id: formulaId,
                    change_count: aiState.changes.length,
                  });
                  aiState.reject();
                }}
                loss={loss}
                targetProfile={targetProfile}
                currentProfile={currentProfileVec}
                errorMessage={aiState.errorMessage}
              />
            )}

            <div
              style={{
                flex: 1,
                overflow: "auto",
                display: "flex",
                flexDirection: "column",
                position: "relative",
              }}
            >
              {aiActive && <ScanBeam />}
              {aiActive ? (
                <div className="vstack" style={{ flex: 1, minHeight: 0 }}>
                  {/* Flavor heatmap stack — every flask row replaced with its
                      111-dim vector profile. The row currently being mutated
                      pulses via HeatmapRow's `af-pulse` class. */}
                  <div
                    className="hstack"
                    style={{
                      padding: "10px 16px",
                      alignItems: "baseline",
                      gap: 8,
                      minHeight: 44,
                    }}
                  >
                    <span
                      className="label"
                      style={{ margin: 0, color: "var(--ink)" }}
                    >
                      Vector profile · Flavor
                    </span>
                    <span
                      className="mono"
                      style={{ fontSize: 9.5, color: "var(--ink-4)" }}
                    >
                      · {formulation.length} substance{formulation.length === 1 ? "" : "s"} ·
                      111 dims · clipped at 0
                    </span>
                  </div>
                  {formulation.map((f) => {
                    const c = compoundById(f.compoundId);
                    if (!c) return null;
                    return (
                      <HeatmapRow
                        key={f.compoundId}
                        name={c.name}
                        family={c.family}
                        kind={c.kind === "substance" ? "substance" : "compound"}
                        vector={lookupAfProfile(f.compoundId) ?? []}
                      />
                    );
                  })}
                  {foundation.length > 0 && (
                    <Fragment>
                      <div
                        className="hstack"
                        style={{
                          padding: "10px 16px",
                          alignItems: "baseline",
                          gap: 8,
                          minHeight: 44,
                          borderTop: "1px solid var(--line)",
                        }}
                      >
                        <span
                          className="label"
                          style={{ margin: 0, color: "var(--ink)" }}
                        >
                          Vector profile · Fixed ingredients
                        </span>
                        <span
                          className="mono"
                          style={{ fontSize: 9.5, color: "var(--ink-4)" }}
                        >
                          · {foundation.length} item{foundation.length === 1 ? "" : "s"} · locked
                        </span>
                      </div>
                      {foundation.map((f) => {
                        const c = compoundById(f.compoundId);
                        if (!c) return null;
                        return (
                          <HeatmapRow
                            key={f.compoundId}
                            name={c.name}
                            family={c.family}
                            kind={c.kind === "substance" ? "substance" : "compound"}
                            vector={lookupAfProfile(f.compoundId) ?? []}
                            dim
                          />
                        );
                      })}
                    </Fragment>
                  )}
                  {/* Live status feed pinned at the bottom of the scroll area. */}
                  {aiState && (
                    <div style={{ marginTop: "auto" }}>
                      <StatusFeed entries={aiState.statusLog} />
                    </div>
                  )}
                </div>
              ) : (
              <Fragment>
              <div>
                {/* Sticky group: "Flavor · N items" + lens tabs + column
                    headers. Pinned to the top of the scroll container so
                    the section label and the Group-by-view tabs stay
                    visible while the user scrolls through the flask rows.
                    When the user scrolls past the entire Flavor block
                    into the Foundation block below, this sticky element
                    exits the viewport with its parent <div>, and the
                    Foundation section's own sticky header takes over —
                    natural CSS sticky stacking semantics, no JS. */}
                <div className="flask-sticky-head">
                <div className="flask-section-head">
                  <div
                    className="hstack"
                    style={{ alignItems: "baseline", gap: 8 }}
                  >
                    <span
                      className="label"
                      style={{ margin: 0, color: "var(--ink)" }}
                    >
                      Flavor
                    </span>
                    <span
                      className="mono"
                      style={{
                        fontSize: 10,
                        color: "var(--ink-4)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      ·  {formulation.length} item
                      {formulation.length === 1 ? "" : "s"}
                    </span>
                  </div>
                  <div className="lens-tabs">
                    {LENS_TABS.map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => {
                          if (lens !== t.id) {
                            logEvent("flavor_workbench_lens_tab_changed", {
                              flavor_id: flavorId,
                              formula_id: formulaId,
                              lens: t.id,
                            });
                          }
                          setLens(t.id);
                        }}
                        className={cn("lens-tab", lens === t.id && "active")}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flask-col-head">
                  {aiReviewing && <span style={{ width: 22, flexShrink: 0 }} />}
                  <span style={{ width: 4, flexShrink: 0 }} />
                  <span style={{ flex: 1 }}>Substance</span>
                  <span
                    style={{
                      width: aiReviewing ? 150 : 110,
                      textAlign: "right",
                      paddingRight: 34,
                    }}
                  >
                    Conc.
                  </span>
                  <span style={{ width: 50, textAlign: "right" }}>% mix</span>
                  {!compact && (
                    <Fragment>
                      <span style={{ width: 16, flexShrink: 0 }} />
                      <span style={{ width: 96, textAlign: "right" }}>
                        Info
                      </span>
                      <span style={{ width: 16, flexShrink: 0 }} />
                    </Fragment>
                  )}
                  {!locked && (
                    <span style={{ width: 56, textAlign: "right" }}>Actions</span>
                  )}
                </div>
                </div>{/* /flask-sticky-head */}

                {groups.map((g) => (
                  <div key={g.key}>
                    <div className="flask-group-head">
                      <span>{g.label}</span>
                      <span style={{ color: "var(--ink-4)" }}>
                        {g.rows.length} ·{" "}
                        {g.rows.reduce((s, r) => s + r.f.ppm, 0).toFixed(1)} ppm
                      </span>
                    </div>
                    {g.rows.map((r) => {
                      const aiChange = aiChangesByCompoundId.get(r.f.compoundId);
                      const rowPerItem = perItemByCompoundId[r.f.compoundId];
                      return (
                        <Fragment key={r.f.compoundId}>
                          <FlaskRowWithBadge
                            entry={r.f}
                            locked={locked}
                            grandTotal={grandTotal}
                            showLensTag={lens === "role" || lens === "solvent"}
                            unit={units[r.f.compoundId]}
                            preview={preview}
                            expanded={expandedFor === r.f.compoundId}
                            target={target}
                            compact={compact}
                            flags={flagsBySubstance[r.f.compoundId] ?? null}
                            flagsLoading={isFlagLoading(r.f.compoundId)}
                            onChangeConc={onChangeConcAndClearOverride}
                            onCycleUnit={cycleUnit}
                            onRemove={onRemoveWithAnalytics}
                            onToggleReplace={toggleExpand}
                            onOpenSupply={openSupply}
                            aiReviewing={aiReviewing}
                            aiChange={
                              aiChange
                                ? {
                                    kind: aiChange.kind,
                                    prevPpm: aiChange.prevPpm,
                                    newPpm: aiChange.newPpm,
                                  }
                                : null
                            }
                            onUndoAIChange={aiState?.undoOne}
                            perItem={rowPerItem}
                            formulaId={formulaId}
                          />
                          {expandedFor === r.f.compoundId && !aiReviewing && (
                            <ReplacementSubRows
                              compoundId={r.f.compoundId}
                              target={target}
                              activePreview={
                                preview && preview.fromId === r.f.compoundId
                                  ? preview
                                  : null
                              }
                              onPreviewSelect={(p) => {
                                logEvent("flavor_workbench_replacement_previewed", {
                                  flavor_id: flavorId,
                                  formula_id: formulaId,
                                  kind: p.isDrop ? "drop" : "swap",
                                  from_compound_id: p.fromId,
                                  to_compound_ids: p.toIds.join(",") || null,
                                });
                                onPreview(p);
                              }}
                              onClose={closeExpand}
                              onOpenSupply={openSupply}
                              baseLoss={rowPerItem?.baseLoss ?? null}
                              dropOnlyLoss={rowPerItem?.dropOnlyLoss ?? null}
                              singles={rowPerItem?.singles}
                              multis={rowPerItem?.multis}
                              flags={flagsBySubstance[r.f.compoundId] ?? null}
                              flagsByCandidate={flagsBySubstance}
                            />
                          )}
                        </Fragment>
                      );
                    })}
                  </div>
                ))}

                {formulation.length === 0 && (
                  <div className="flask-empty">
                    Empty flavor side. Add compounds from the search panel or
                    accept a recommendation.
                  </div>
                )}

                {/* Ghost rows — substances the AI removed during review.
                    Rendered faded with a strike-through name; an Undo button
                    in the action cell restores them. */}
                {aiReviewing &&
                  aiState &&
                  aiState.changes
                    .filter((ch) => ch.kind === "remove")
                    .map((ch) => {
                      const c = compoundById(ch.compoundId);
                      if (!c) return null;
                      return (
                        <div
                          key={`ghost-${ch.compoundId}`}
                          className="flask-row af-row-remove af-row-ghost"
                        >
                          <span
                            style={{
                              width: 22,
                              flexShrink: 0,
                              display: "inline-flex",
                              justifyContent: "center",
                            }}
                          >
                            <ChangeSymbol kind="remove" />
                          </span>
                          <div
                            className="fam-stripe"
                            style={{
                              width: c.kind === "substance" ? 7 : 4,
                              display: "flex",
                              gap: c.kind === "substance" ? 1 : 0,
                              opacity: 0.4,
                            }}
                          >
                            <div
                              style={{
                                flex: 1,
                                borderRadius: 2,
                                background: `var(--fam-${c.family ?? "neutral"}, var(--fam-default))`,
                              }}
                            />
                          </div>
                          <div
                            className="vstack"
                            style={{ flex: 1, minWidth: 0, lineHeight: 1.2 }}
                          >
                            <div
                              className="hstack gap-2"
                              style={{ alignItems: "center", minWidth: 0 }}
                            >
                              <span
                                className="name af-row-name"
                                style={
                                  c.kind === "substance"
                                    ? {
                                        fontStyle: "italic",
                                        fontFamily:
                                          "var(--font-serif, 'Noto Serif', serif)",
                                      }
                                    : undefined
                                }
                              >
                                {c.name}
                              </span>
                            </div>
                            <span className="desc">{c.cas}</span>
                          </div>
                          <div
                            className="hstack mono"
                            style={{
                              width: 150,
                              gap: 4,
                              justifyContent: "flex-end",
                              flexShrink: 0,
                            }}
                          >
                            <span className="af-conc-prev">
                              {(ch.prevPpm ?? 0).toFixed(1)}
                            </span>
                            <span
                              style={{
                                width: 72,
                                textAlign: "right",
                                padding: "4px 4px",
                                fontSize: 12,
                                color: "var(--ink-3)",
                              }}
                            >
                              0
                            </span>
                            <span className="unit-readonly">ppm</span>
                          </div>
                          <span
                            className="mono"
                            style={{
                              width: 50,
                              textAlign: "right",
                              fontSize: 11,
                              color: "var(--ink-4)",
                            }}
                          >
                            0.0%
                          </span>
                          {!compact && (
                            <Fragment>
                              <span style={{ width: 16, flexShrink: 0 }} />
                              <span style={{ width: 96 }} />
                              <span style={{ width: 16, flexShrink: 0 }} />
                            </Fragment>
                          )}
                          <div
                            className="hstack af-undo-cell"
                            style={{
                              gap: 2,
                              width: 56,
                              justifyContent: "flex-end",
                            }}
                          >
                            <UndoButton
                              kind="remove"
                              onClick={() => aiState.undoOne(ch.compoundId)}
                            />
                          </div>
                        </div>
                      );
                    })}
              </div>

              {foundation.length > 0 && (
                <div className="foundation-section">
                  <div className="foundation-head">
                    <div
                      className="hstack"
                      style={{ alignItems: "baseline", gap: 8 }}
                    >
                      <span
                        className="label"
                        style={{ margin: 0, color: "var(--ink)" }}
                      >
                        Fixed ingredients
                      </span>
                      <span
                        className="mono"
                        style={{
                          fontSize: 10,
                          color: "var(--ink-4)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        ·  {foundation.length} item
                        {foundation.length === 1 ? "" : "s"}
                      </span>
                    </div>
                  </div>
                  {sortedFoundation.map((f) => (
                    <FlaskRow
                      key={f.compoundId}
                      entry={f}
                      isFoundation
                      grandTotal={grandTotal}
                      showLensTag={false}
                      unit={units[f.compoundId]}
                      preview={preview}
                      target={target}
                      compact={compact}
                      flags={flagsBySubstance[f.compoundId] ?? null}
                      flagsLoading={isFlagLoading(f.compoundId)}
                      onChangeConc={onChangeConcAndClearOverride}
                      onCycleUnit={cycleUnit}
                      onRemove={onRemoveWithAnalytics}
                      onToggleReplace={() => undefined}
                      onOpenSupply={openSupply}
                      aiReviewing={aiReviewing}
                      badge={{ kind: "disabled" }}
                    />
                  ))}
                </div>
              )}
              </Fragment>
              )}
            </div>
          </div>
        </div>
      </div>
      <SupplyModalHost state={supplyModal} onClose={closeSupply} />
    </Fragment>
  );
}
